import { useEffect, useState } from "react";
import { Button, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { FiltersComponent } from "../filters.component";
import { useLoadData } from "../../../hooks/useLoadData";
import { ApiResponse } from "../../../models/api-response";
import { useReactHookForm } from "../../../hooks/useReactHookForm";
import { UserPermissionName } from "../../../models/user-permission-name";
import { SiTrackGrid } from "../../../components/si-track-grid/si-track-grid";
import { planReviewApiKeyStore } from "../../../api/plan-review-api-key-store";
import { SelectResult } from "../../../components/select/select-chain.component";
import { AssignedToDialog } from "../plan-review-task-assinged/assigned-to.dialog";
import { planReviewTaskFormSchema } from "../plan-review-task-dialog/plan-form.schema";
import { ReactHookForm } from "../../../components/hook-form/react-hook-form.component";
import { PlanReviewTaskDialog } from "../plan-review-task-dialog/plan-review-task-dialog.compoment";
import { gridApiKeyStore } from "../../../api/grid-api-key-store";
import { useSaveData } from "../../../hooks/useSaveData";
import { GridColumnConfig, GridPreference } from "../../../models/gridCoumnConfig";
import { PlanReview } from "../../../models/planReview";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../../hooks/use-debounce";
import { useAuthorize } from "../../../hooks/use-authorize";


interface CellProps {
    moduleData: any;
}

interface CustomGridCellProps {
    field: string;
}

interface EnergyModelingTab {
    title: string;
    moduleCode: string;
    dashboardColumns: any;
    userRole: any;
    isRoleLoading: boolean;
    handleUserRole: () => void;
}

export const EnergyModelingTab = ({ title, moduleCode, dashboardColumns, userRole, isRoleLoading, handleUserRole }: EnergyModelingTab) => {

    const form = useReactHookForm({
        defaultValues: {
            customer: { customerID: '', customerName: '' },
            office: { officeID: '', officeName: '' },
            project: { projectID: '', projectName: '' },
            unit: { unitID: '', unitName: '' },
            jobsite: { unitID: '', jobsite: '' },
            assignedTo: { unitID: '', fullName: '' },
            status: { planReviewTaskStatusID: '', planReviewTaskStatusName: '' },
            address: ''
        }
    });

    const [isRoleHovered, setIsRoleHovered] = useState(false);
    const [isDialogOpen, setDialogOpen] = useState<any>();
    const [isAssignedToDialogOpen, setAssignedToDialogOpen] = useState<any>();
    const [selectedFilters, setSelectedFilters] = useState<SelectResult>({});
    const [tooltipText, setTooltipText] = useState('Copy Text');

    const { data: EnergyModelings, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
        planReviewApiKeyStore.getPlanReviewDashboards(selectedFilters),
        !!selectedFilters?.customerID ||
        !!selectedFilters?.officeID ||
        !!selectedFilters?.projectID ||
        !!selectedFilters?.unitID ||
        !!selectedFilters?.jobsite ||
        !!selectedFilters?.assignedTo ||
        !!selectedFilters?.status ||
        !!selectedFilters?.address ||
        !!selectedFilters?.includeCompleted ||
        !!selectedFilters?.moduleCode ||
        !!selectedFilters?.roleID ||
        !!selectedFilters?.roleCode
    );

    useEffect(() => {
        if (userRole)
            setSelectedFilters({ ...selectedFilters, moduleCode: moduleCode, roleID: userRole?.roleID, roleCode: userRole?.roleCode });
    }, [userRole])

    const handleEditEnergyModeling = (modelingData: any) => { setDialogOpen(modelingData); }

    const handleSubmitPlanTask = () => {
        setDialogOpen(undefined);
        setAssignedToDialogOpen(undefined);
        refetch();
    }

    const handleSubmit = (filtersData: any) => {
        const filteringData = {
            customerID: filtersData?.customer?.customerID ?? 0,
            officeID: filtersData?.office?.officeID ?? 0,
            projectID: filtersData?.project?.projectID ?? 0,
            unitID: filtersData?.unit?.unitID ?? 0,
            jobsite: filtersData?.jobsite?.unitID ?? 0,
            assignedTo: filtersData?.assignedTo?.userID ?? 0,
            status: filtersData?.status?.planReviewTaskStatusID ?? 0,
            address: filtersData?.address,
            includeCompleted: filtersData?.includeCompleted ?? false,
            roleID: userRole?.roleID,
            roleCode: userRole?.roleCode,
            moduleCode: moduleCode
        };
        setSelectedFilters(filteringData);
    };

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setTooltipText('Copied!');
            setTimeout(() => setTooltipText('Copy Text'), 1500);
        } catch (err) {
            console.error('Failed to copy!', err);
        }
    };

    const JobSiteCell = ({ moduleData }: CellProps) => {
        const [isHovered, setIsHovered] = useState(false);
        const handleClickJobsite = () => {
            window.open(`/units/profile/${moduleData?.unitID}`, "_blank");
        };
        return (
            <td>
                <Typography onClick={handleClickJobsite}
                    component="span"
                    style={{
                        color: isHovered ? '#0000b5' : '#0000b5',
                        textDecoration: isHovered ? 'underline' : 'none',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {moduleData?.jobSite}
                </Typography>
                {/* <IconButton
                    aria-label="close"
                    onClick={() => handleCopy(moduleData?.jobSite)}
                >
                    <Tooltip title={<Typography variant="body1">{tooltipText}</Typography>}>
                        <ContentCopyIcon />
                    </Tooltip>
                </IconButton> */}
            </td>
        );
    };

    const isEnergyModelingPermission = useAuthorize([UserPermissionName?.EDIT_ENERGY_MODELING_DASHBOARD]);

    const AssignedToCell = ({ moduleData }: CellProps) => {
        const [isHovered, setIsHovered] = useState(false);
        const handleClickUnit = () => { setAssignedToDialogOpen(moduleData) };
        return (
            <td>
                {
                    moduleData?.assignee ? (
                        isEnergyModelingPermission ?
                            <Typography onClick={handleClickUnit}
                                component="span"
                                style={{
                                    color: isHovered ? '#0000b5' : '#0000b5',
                                    textDecoration: isHovered ? 'underline' : 'none',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                            >
                                {moduleData?.assignee}
                            </Typography>
                            :
                            <Typography component="span">{moduleData?.assignee}</Typography>
                    ) :
                        <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleClickUnit()} disabled={!isEnergyModelingPermission}>
                            <Tooltip title={<Typography variant="body1">Assign</Typography>}>
                                <AssignmentTurnedInOutlinedIcon className="btnAssgined" />
                            </Tooltip>
                        </IconButton>
                }
            </td>
        );
    };

    const CustomGridCell = ({ field }: CustomGridCellProps) => {
        if (field === 'jobSite') {
            return (dataItem: any) => {
                return <JobSiteCell moduleData={dataItem.dataItem} />;
            };
        }
        if (field === 'assignee') {
            return (dataItem: any) => {
                return <AssignedToCell moduleData={dataItem.dataItem} />;
            };
        }
    }

    // -------------------------------------------------Grid Layout------------------------------

    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout('EnergyModeling', 'EnergyModeling')
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredEnergyModelings, setFilteredEnergyModelings] = useState<PlanReview[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredEnergyModelingList = EnergyModelings?.data?.filter((EnergyModeling) =>
            Object.values(EnergyModeling).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredEnergyModelings(filteredEnergyModelingList || []);
    }, [debouncedSearchTerm, EnergyModelings?.data]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: 'EnergyModeling',
            pageName: 'EnergyModeling',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: 'EnergyModeling',
            pageName: 'EnergyModeling',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    return (
        <div className="borderTop">
            <Typography
                className="innerTab-button"
                variant="body1"
            >

                <Grid
                    container
                    direction="row"
                    sx={{
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                    }}
                >

                    Role:
                    <div
                        onClick={handleUserRole}
                        style={{
                            paddingLeft: 3,
                            color: isRoleHovered ? '#0000b5' : '#0000b5',
                            textDecoration: isRoleHovered ? 'underline' : 'none',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={() => setIsRoleHovered(true)}
                        onMouseLeave={() => setIsRoleHovered(false)}>
                        {userRole?.roleName ?? 'Select Role'}
                    </div>
                </Grid>
            </Typography>
            {/* <GuardedComponent requiredPermissions={[UserPermissionName.ADD_PLAN]}> */}
            {/* <Button
                
                size="small"
                variant="contained"
                disableElevation={true}
                startIcon={<AddIcon />}
            onClick={handleCreatePlan}
            >
                Add Lot
            </Button> */}
            {/* </GuardedComponent> */}
            <ReactHookForm formName="PLAN_REVIEW_TASK_FILTERS_FORM" onSubmit={handleSubmit} formContextProps={form}>
                <Grid container spacing={2}>
                    <Grid item xl={12} lg={12} sm={12} xs={12}>
                        <FiltersComponent moduleCode={moduleCode} />
                    </Grid>
                    <Grid item xl={12} lg={12} sm={12} xs={12}>
                        <SiTrackGrid
                            fileTitle='energy_modeling'
                            columns={dashboardColumns}
                            dataItemKey="planReviewTaskID"
                            customCell={(field) =>
                                CustomGridCell({
                                    field,
                                })
                            }
                            onEdit={handleEditEnergyModeling}
                            deleteMutationConfig={(item) => planReviewApiKeyStore.deletePlanReviewTask(item.planReviewTaskID)}
                            actionPermissions={{
                                edit: UserPermissionName.EDIT_ENERGY_MODELING_DASHBOARD,
                                delete: UserPermissionName.DELETE_ENERGY_MODELING_DASHBOARD,
                                export: UserPermissionName.DOWNLOAD_GRID
                            }}
                            isBusy={isLoading || isFetching || isLoadingGridLayout || isRoleLoading}

                            // -----------------Grid Layout-------
                            dataItems={filteredEnergyModelings || []}
                            reorderable
                            handleSearch={handleInputChange}
                            handleSaveLayout={handleSaveLayout}
                            isSaveLayout={true}
                            gridConfigFromApi={gridConfig}
                            handleResetLayout={handleResetLayout}
                        // -----------------Grid Layout-------
                        />
                    </Grid>
                </Grid>
            </ReactHookForm>
            {isDialogOpen && (
                <PlanReviewTaskDialog
                    title={title}
                    moduleCode={moduleCode}
                    planReviewData={isDialogOpen}
                    planReviewSchema={planReviewTaskFormSchema}
                    onSubmit={handleSubmitPlanTask}
                    parentRefetch={refetch}
                    onClosed={() => setDialogOpen(undefined)} />
            )}
            {isAssignedToDialogOpen && (
                <AssignedToDialog
                    moduleCode={moduleCode}
                    planReviewData={isAssignedToDialogOpen}
                    onSubmit={handleSubmitPlanTask}
                    onClosed={() => setAssignedToDialogOpen(undefined)} />
            )}
        </div>
    )
}